import { Controller } from '@hotwired/stimulus'
import Sortable from 'sortablejs'

export default class extends Controller {
  static targets = ['form', 'toggleLink', 'activeList', 'activeCheckbox', 'availableCheckbox', 'dragHandle',
                    'availableSection', 'availableCore', 'availableGroup', 'availableRow']

  connect() {
    this.initializeSortable()
    this.updateToggleLinkStates()
  }

  disconnect() {
    if (this.sortableInstance) {
      this.sortableInstance.destroy()
      this.sortableInstance = null
    }
  }

  initializeSortable() {
    if (!this.hasActiveListTarget) return

    this.sortableInstance = new Sortable(this.activeListTarget, {
      animation: 150,
      draggable: '.active-column-item',
      handle: '.column-drag-handle',
      ghostClass: 'column-drag-ghost',
      chosenClass: 'column-drag-chosen',
      forceFallback: true, // Use fallback to avoid conflicts with Bootstrap dropdown
      fallbackClass: 'column-drag-fallback',

      onEnd: () => {
        // Reorder is handled by form submission - checkboxes are already in DOM order
      }
    })
  }

  toggle() {
    // Individual column toggle - update toggle link text for the group
    this.updateToggleLinkStates()
  }

  // Fired (via window) by tasks-beta-type-filters whenever the Task Type
  // select changes, so the Available section narrows without waiting for
  // the filters to be applied. Server-rendered initial state matches.
  onTaskTypesChanged(event) {
    this.narrowToTypes(event.detail?.types || [])
  }

  narrowToTypes(types) {
    this.availableRowTargets.forEach(row => {
      const rowTypes = (row.dataset.taskTypes || '').split(' ')
      const visible = types.length === 0 || rowTypes.some(type => types.includes(type))

      row.classList.toggle('d-none', !visible)
      // Disabled so a column ticked-then-narrowed-away doesn't submit
      row.querySelectorAll('input').forEach(el => { el.disabled = !visible })
    })

    this.availableGroupTargets.forEach(group => {
      const anyRowVisible = !!group.querySelector('[data-tasks-columns-dropdown-target~="availableRow"]:not(.d-none)')
      group.classList.toggle('d-none', !anyRowVisible)
    })

    if (this.hasAvailableSectionTarget) {
      const anyGroupVisible = this.availableGroupTargets.some(group => !group.classList.contains('d-none'))
      this.availableSectionTarget.classList.toggle('d-none', !(this.hasAvailableCoreTarget || anyGroupVisible))
    }

    this.updateToggleLinkStates()
  }

  toggleAll(event) {
    event.preventDefault()
    const link = event.currentTarget
    const taskType = link.dataset.taskType
    const checkboxes = this.availableCheckboxTargets.filter(cb => cb.dataset.taskType === taskType)
    const anyChecked = checkboxes.some(cb => cb.checked)

    // If any are checked, deselect all; otherwise select all
    checkboxes.forEach(cb => {
      cb.checked = !anyChecked
    })

    this.updateToggleLinkStates()
  }

  updateToggleLinkStates() {
    // Group available checkboxes by task type
    const groupedByType = {}
    this.availableCheckboxTargets.forEach(cb => {
      const taskType = cb.dataset.taskType
      if (taskType) {
        if (!groupedByType[taskType]) {
          groupedByType[taskType] = []
        }
        groupedByType[taskType].push(cb)
      }
    })

    // Update each toggle link text based on its group's state
    this.toggleLinkTargets.forEach(link => {
      const taskType = link.dataset.taskType
      const checkboxes = groupedByType[taskType] || []
      const anyChecked = checkboxes.some(cb => cb.checked)

      link.textContent = anyChecked ? 'Deselect all' : 'Select all'
    })
  }
}
